import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Tabs,
  Tab,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Security as SecurityIcon,
  PrivacyTip as PrivacyIcon,
  CheckCircle as CheckCircleIcon,
  VerifiedUser as VerifiedUserIcon,
  Description as DescriptionIcon,
  Lock as LockIcon,
  ArrowForward as ArrowForwardIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import {
  ismsRequirements,
  iso27001Controls,
  privacyControls,
  getAllCategories
} from '../../data/trustCentreData';

const TrustCentre = ({ isMobile, theme }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [tabValue, setTabValue] = useState(0);

  // Documents data
  const featuredDocuments = [
    { 
      id: 1, 
      name: 'ISO 27001:2022 Attestation Letter', 
      type: 'Attestation', 
      access: 'Public',
      url: 'https://c-encode-assets-public.s3.ca-central-1.amazonaws.com/ISO+Attestation+Letter.pdf',
      isPublic: true
    },
    { 
      id: 2, 
      name: 'PHIPA Attestation Letter', 
      type: 'Attestation', 
      access: 'Public',
      url: 'https://c-encode-assets-public.s3.ca-central-1.amazonaws.com/PHIPA+Attestation+Letter.pdf',
      isPublic: true
    },
    { 
      id: 3, 
      name: 'Privacy Policy', 
      type: 'Policy', 
      access: 'Public',
      url: 'https://c-encode-assets-public.s3.ca-central-1.amazonaws.com/Privacy+Policy.pdf',
      isPublic: true
    }
  ];

  const allDocuments = [
    { 
      id: 1, 
      name: 'ISO 27001:2022 Attestation Letter', 
      type: 'Attestation', 
      date: '2024-01-15', 
      access: 'Public',
      url: 'https://c-encode-assets-public.s3.ca-central-1.amazonaws.com/ISO+Attestation+Letter.pdf',
      isPublic: true
    },
    { 
      id: 2, 
      name: 'PHIPA Attestation Letter', 
      type: 'Attestation', 
      date: '2024-02-20', 
      access: 'Public',
      url: 'https://c-encode-assets-public.s3.ca-central-1.amazonaws.com/PHIPA+Attestation+Letter.pdf',
      isPublic: true
    },
    { 
      id: 3, 
      name: 'Privacy Policy', 
      type: 'Policy', 
      date: '2024-03-10', 
      access: 'Public',
      url: 'https://c-encode-assets-public.s3.ca-central-1.amazonaws.com/Privacy+Policy.pdf',
      isPublic: true
    },
    { 
      id: 4, 
      name: 'Security Overview', 
      type: 'Document', 
      date: '2024-04-05', 
      access: 'Request access',
      email: 'security@c-encode.com',
      isPublic: false
    }
  ];

  const handleDocumentClick = (doc) => {
    if (doc.isPublic && doc.url) {
      window.open(doc.url, '_blank');
    } else if (doc.email) {
      const subject = encodeURIComponent(doc.name);
      const mailtoLink = `mailto:${doc.email}?subject=Trust Centre Document Request: ${subject}`;
      window.location.href = mailtoLink;
    }
  };

  // Get privacy categories (PIPEDA principles)
  const getPrivacyCategories = () => {
    return [...new Set(privacyControls.map(control => control.category))];
  };

  const categories = tabValue === 1 ? ['All', 'ISMS Requirements', ...getAllCategories()] : (tabValue === 2 ? ['All', ...getPrivacyCategories()] : []);

  // Filter and group controls by type (Requirements vs Controls) and category
  const groupedControls = useMemo(() => {
    // Get controls for current tab
    const allControls = tabValue === 1 ? [...ismsRequirements, ...iso27001Controls] : (tabValue === 2 ? privacyControls : []);
    let filtered = allControls;

    // Apply category filter
    if (selectedCategory !== 'All' && selectedCategory !== 'ISMS Requirements') {
      filtered = filtered.filter(control => control.category === selectedCategory);
    }

    // Apply search filter
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(control =>
        control.id.toLowerCase().includes(lowerQuery) ||
        control.name.toLowerCase().includes(lowerQuery) ||
        control.description.toLowerCase().includes(lowerQuery)
      );
    }

    // For privacy controls (tabValue === 2), group directly by category
    if (tabValue === 2) {
      const grouped = {};
      filtered.forEach(control => {
        const category = control.category || 'Privacy';
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(control);
      });
      return grouped;
    }

    // For ISO 27001 controls (tabValue === 1), group by type first, then by category
    const grouped = {
      Requirements: {},
      Controls: {}
    };
    
    filtered.forEach(control => {
      const type = control.type === 'Requirement' ? 'Requirements' : 'Controls';
      const category = control.category || 'Privacy';
      if (!grouped[type][category]) {
        grouped[type][category] = [];
      }
      grouped[type][category].push(control);
    });

    return grouped;
  }, [searchQuery, selectedCategory, tabValue]);

  // Get categories to display (either all categories or just the selected one)
  const categoriesToDisplay = useMemo(() => {
    if (selectedCategory !== 'All') {
      return [selectedCategory];
    }
    // For ISO 27001, show all categories; for Privacy, show all PIPEDA principles
    if (tabValue === 1) {
      return getAllCategories();
    }
    if (tabValue === 2) {
      return getPrivacyCategories();
    }
    return [];
  }, [selectedCategory, tabValue]);

  return (
    <div style={{
      fontFamily: 'Open Sans, Arial, sans-serif',
      backgroundColor: '#FFFFFF',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header Section */}
      <Box sx={{
        backgroundColor: '#0A3D62',
        color: '#F5F5F5',
        padding: isMobile ? '30px 20px' : '40px',
        borderBottom: '1px solid #E0E0E0'
      }}>
        <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
          <Typography variant="h4" sx={{
            fontFamily: 'Roboto, Arial, sans-serif',
            fontWeight: 'bold',
            marginBottom: '10px',
            fontSize: isMobile ? '1.5rem' : '2rem'
          }}>
            Trust Centre
          </Typography>
          <Typography variant="body1" sx={{
            fontSize: isMobile ? '0.9rem' : '1rem',
            opacity: 0.9
          }}>
            ISO 27001 ISMS and Privacy Compliance
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{
        borderBottom: '1px solid #E0E0E0',
        backgroundColor: '#FFFFFF',
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%'
      }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => {
            setTabValue(newValue);
            setSelectedCategory('All');
            setSearchQuery('');
          }}
          sx={{
            '& .MuiTab-root': {
              color: '#666',
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              fontWeight: 500,
              textTransform: 'none',
              minHeight: '48px',
              padding: '12px 24px'
            },
            '& .Mui-selected': {
              color: '#0A3D62',
              fontWeight: 600
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0A3D62',
              height: '3px'
            }
          }}
        >
          <Tab
            icon={<VerifiedUserIcon sx={{ fontSize: '20px' }} />}
            iconPosition="start"
            label="Compliance"
          />
          <Tab
            icon={<SecurityIcon sx={{ fontSize: '20px' }} />}
            iconPosition="start"
            label="Security Controls"
          />
          <Tab
            icon={<PrivacyIcon sx={{ fontSize: '20px' }} />}
            iconPosition="start"
            label="Privacy Controls"
          />
          <Tab
            icon={<DescriptionIcon sx={{ fontSize: '20px' }} />}
            iconPosition="start"
            label="Documents"
          />
        </Tabs>
      </Box>

      {/* Main Content Area */}
      <Box sx={{
        flex: 1,
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%',
        padding: isMobile ? '20px' : '30px 40px',
        backgroundColor: '#FFFFFF'
      }}>
        {/* Compliance Tab */}
        {tabValue === 0 && (
          <Box>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '30px', marginBottom: '40px' }}>
              {/* Left Column - Compliance Overview */}
              <Box sx={{ flex: 1 }}>
                <Paper elevation={0} sx={{ padding: '25px', backgroundColor: '#FAFAFA', marginBottom: '30px', border: '1px solid #E0E0E0' }}>
                  <Typography variant="h6" sx={{
                    fontFamily: 'Roboto, Arial, sans-serif',
                    color: '#0A3D62',
                    fontWeight: 600,
                    marginBottom: '8px',
                    fontSize: '1.1rem'
                  }}>
                    Compliance Overview
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', marginBottom: '20px', fontSize: '0.875rem' }}>
                    Current compliance status across frameworks
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {[
                      { name: 'ISO 27001:2022', status: 'Compliant', color: '#4caf50', tab: 1 },
                      { name: 'PIPEDA', subtitle: 'Federal private-sector baseline', status: 'Compliant', color: '#4caf50', tab: 2 },
                      { 
                        name: 'Substantially Similar Health Privacy Acts', 
                        subtitle: 'PHIPA (ON), PHIPAA (NB), PHIA (NS, NL, MB)',
                        status: 'Compliant', 
                        color: '#4caf50', 
                        tab: 2 
                      },
                      { 
                        name: 'Provincial Health-Information Statutes', 
                        subtitle: 'HIA (AB), HIPA (SK), LSSSS (QC)',
                        status: 'Compliant', 
                        color: '#4caf50', 
                        tab: 2 
                      }
                    ].map((framework) => (
                      <Box 
                        key={framework.name} 
                        onClick={() => {
                          setTabValue(framework.tab);
                          setSelectedCategory('All');
                          setSearchQuery('');
                        }}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '15px',
                          backgroundColor: '#FFFFFF',
                          borderRadius: '8px',
                          border: '1px solid #E0E0E0',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#F5F5F5',
                            borderColor: '#0A3D62'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                          <Box sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#0A3D62',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#FFFFFF',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            textAlign: 'center',
                            padding: '2px',
                            flexShrink: 0
                          }}>
                            {framework.name.includes('ISO') ? 'ISO' : 
                             framework.name.includes('Substantially') ? 'PHIPA' :
                             framework.name.includes('PIPEDA') ? 'PIPEDA' :
                             framework.name.includes('Provincial') ? 'PHI' :
                             framework.name.substring(0, 2)}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#333', fontSize: '0.95rem' }}>
                              {framework.name}
                            </Typography>
                            {framework.subtitle && (
                              <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem', marginTop: '2px' }}>
                                {framework.subtitle}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CheckCircleIcon sx={{ color: framework.color, fontSize: '20px' }} />
                          <Typography variant="body2" sx={{ color: framework.color, fontWeight: 500 }}>
                            {framework.status}
                          </Typography>
                          <ArrowForwardIcon sx={{ color: '#999', fontSize: '18px', marginLeft: '8px' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>

                {/* Featured Documents */}
                <Paper elevation={0} sx={{ padding: '25px', backgroundColor: '#FAFAFA', border: '1px solid #E0E0E0' }}>
                  <Typography variant="h6" sx={{
                    fontFamily: 'Roboto, Arial, sans-serif',
                    color: '#0A3D62',
                    fontWeight: 600,
                    marginBottom: '8px',
                    fontSize: '1.1rem'
                  }}>
                    Featured Documents
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', marginBottom: '20px', fontSize: '0.875rem' }}>
                    Key security and compliance documentation
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {featuredDocuments.map((doc) => (
                      <Box 
                        key={doc.id} 
                        onClick={() => handleDocumentClick(doc)}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '15px',
                          backgroundColor: '#FFFFFF',
                          borderRadius: '8px',
                          border: '1px solid #E0E0E0',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#F5F5F5',
                            borderColor: '#0A3D62'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <DescriptionIcon sx={{ color: '#0A3D62', fontSize: '24px' }} />
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#333', fontSize: '0.9rem' }}>
                              {doc.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#999', fontSize: '0.75rem' }}>
                              {doc.type}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: doc.isPublic ? '#4caf50' : '#FF6B35' }}>
                          {doc.isPublic ? (
                            <>
                              <CheckCircleIcon sx={{ fontSize: '16px' }} />
                              <OpenInNewIcon sx={{ fontSize: '14px', opacity: 0.7 }} />
                            </>
                          ) : (
                            <LockIcon sx={{ fontSize: '16px' }} />
                          )}
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                            {doc.access}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Box>

              {/* Right Column - Compliance Program Overview */}
              <Box sx={{ flex: 1 }}>
                <Paper elevation={0} sx={{ padding: '25px', backgroundColor: '#FAFAFA', border: '1px solid #E0E0E0' }}>
                  <Typography variant="h6" sx={{
                    fontFamily: 'Roboto, Arial, sans-serif',
                    color: '#0A3D62',
                    fontWeight: 600,
                    marginBottom: '8px',
                    fontSize: '1.1rem'
                  }}>
                    Compliance Program
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', marginBottom: '20px', fontSize: '0.875rem' }}>
                    An overview of security and privacy controls in place
                  </Typography>
                  <Typography variant="body1" sx={{
                    color: '#333',
                    lineHeight: 1.8,
                    fontSize: '0.9rem',
                    marginBottom: '20px'
                  }}>
                    Our comprehensive Information Security Management System (ISMS) aligns with ISO 27001:2022, providing robust security controls across organizational, people, physical, and technological domains. We maintain 93 security controls organized into four key categories to protect information assets, ensure system availability, and prevent unauthorized access.
                  </Typography>
                  <Typography variant="body1" sx={{
                    color: '#333',
                    lineHeight: 1.8,
                    fontSize: '0.9rem',
                    marginBottom: '20px'
                  }}>
                    Our privacy program meets the requirements of PIPEDA and provincial health-information privacy acts across Canada, ensuring comprehensive protection of personal and health information. We maintain 106 privacy controls organized under the 10 PIPEDA principles, covering accountability, consent management, data minimization, accuracy, safeguards, and individual rights.
                  </Typography>
                  <Typography variant="body1" sx={{
                    color: '#333',
                    lineHeight: 1.8,
                    fontSize: '0.9rem',
                    marginBottom: '15px'
                  }}>
                    We maintain compliance with:
                  </Typography>
                  <Box component="ul" sx={{
                    color: '#333',
                    lineHeight: 1.8,
                    fontSize: '0.9rem',
                    marginLeft: '20px',
                    marginBottom: '20px',
                    paddingLeft: '10px'
                  }}>
                    <li style={{ marginBottom: '10px' }}>
                      <strong>ISO 27001:2022</strong> — Information Security Management System with 93 security controls;
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <strong>PIPEDA</strong> (federal private-sector baseline);
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <strong>PHIPA (ON), PHIPAA (NB), PHIA (NS, NL, MB)</strong> — acts deemed substantially similar to PIPEDA;
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <strong>HIA (AB), HIPA (SK), and LSSSS (QC)</strong> — provincial health-information statutes with additional safeguards or breach-reporting obligations.
                    </li>
                  </Box>
                  <Typography variant="body1" sx={{
                    color: '#333',
                    lineHeight: 1.8,
                    fontSize: '0.9rem',
                    marginBottom: '20px'
                  }}>
                    These frameworks collectively ensure comprehensive protection of personal and health information through rigorous security controls, consent management, breach notification procedures, audit logging, and data-subject rights across all jurisdictions in which we operate.
                  </Typography>
                  <Box sx={{
                    padding: '15px',
                    backgroundColor: '#E3F2FD',
                    borderRadius: '8px',
                    border: '1px solid #0A3D62'
                  }}>
                    <Typography variant="body2" sx={{ color: '#0A3D62', fontWeight: 500, fontSize: '0.85rem' }}>
                      All controls are regularly reviewed, audited, and updated to maintain compliance and address emerging security and privacy threats.
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        )}

        {/* ISO 27001 Controls Tab */}
        {tabValue === 1 && (
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
            {/* Left Sidebar - Categories */}
            <Box sx={{
              width: isMobile ? '100%' : '250px',
              backgroundColor: '#FAFAFA',
              borderRight: isMobile ? 'none' : '1px solid #E0E0E0',
              borderBottom: isMobile ? '1px solid #E0E0E0' : 'none',
              minHeight: isMobile ? 'auto' : 'calc(100vh - 200px)',
              padding: '20px 0',
              marginRight: isMobile ? 0 : '30px',
              marginBottom: isMobile ? '20px' : 0
            }}>
              <Typography variant="h6" sx={{
                padding: '0 20px 15px 20px',
                color: '#0A3D62',
                fontFamily: 'Roboto, Arial, sans-serif',
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Controls
              </Typography>
              <List sx={{ padding: 0 }}>
                {categories.map((category, index) => (
                  <React.Fragment key={category}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => setSelectedCategory(category)}
                        selected={selectedCategory === category}
                        sx={{
                          padding: '12px 20px',
                          '&.Mui-selected': {
                            backgroundColor: '#E3F2FD',
                            borderLeft: '3px solid #0A3D62',
                            paddingLeft: '17px'
                          },
                          '&:hover': {
                            backgroundColor: '#F5F5F5'
                          }
                        }}
                      >
                        <ListItemText
                          primary={category}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: selectedCategory === category ? 600 : 400,
                            color: selectedCategory === category ? '#0A3D62' : '#333'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < categories.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>

            {/* Main Content Area */}
            <Box sx={{ flex: 1 }}>
              {/* Category Title and Search */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '30px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '20px'
              }}>
                <Typography variant="h5" sx={{
                  fontFamily: 'Roboto, Arial, sans-serif',
                  color: '#0A3D62',
                  fontWeight: 600,
                  fontSize: isMobile ? '1.3rem' : '1.5rem'
                }}>
                  {selectedCategory === 'All' ? 'All Controls' : selectedCategory}
                </Typography>
                <TextField
                  placeholder="Search controls"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#999', fontSize: '20px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: isMobile ? '100%' : '300px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#FAFAFA',
                      '&:hover fieldset': {
                        borderColor: '#0A3D62',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0A3D62',
                      },
                    },
                  }}
                />
              </Box>

              {/* Controls Tables - Separated by Requirements and Controls */}
              {(() => {
                // Get all requirements directly from the data array to preserve order
                let allRequirements = ismsRequirements;

                // Apply category filter if needed (but not if "ISMS Requirements" is selected - show all)
                if (selectedCategory !== 'All' && selectedCategory !== 'ISMS Requirements') {
                  allRequirements = allRequirements.filter(req => req.category === selectedCategory);
                }

                // Apply search filter if needed
                if (searchQuery) {
                  const lowerQuery = searchQuery.toLowerCase();
                  allRequirements = allRequirements.filter(req =>
                    req.id.toLowerCase().includes(lowerQuery) ||
                    req.name.toLowerCase().includes(lowerQuery) ||
                    req.description.toLowerCase().includes(lowerQuery)
                  );
                }

                // Only show ISMS Requirements section if:
                // - "All" is selected (show everything)
                // - "ISMS Requirements" is selected (show only requirements)
                // Do NOT show when a specific category is selected (only show Annex A Controls)
                const shouldShowRequirements = (selectedCategory === 'All' || selectedCategory === 'ISMS Requirements') && allRequirements.length > 0;

                // Check if there are any Annex A Controls to show
                const hasControls = selectedCategory !== 'ISMS Requirements' && categoriesToDisplay.some(cat => groupedControls.Controls[cat] && groupedControls.Controls[cat].length > 0);

                // Show "No controls found" only if neither requirements nor controls are available
                if (!shouldShowRequirements && !hasControls) {
                  return (
                    <Paper elevation={0} sx={{ padding: '60px', textAlign: 'center', backgroundColor: '#FAFAFA' }}>
                      <Typography variant="h6" sx={{ color: '#666', marginBottom: '10px' }}>
                        No controls found
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        Try adjusting your search or category filter.
                      </Typography>
                    </Paper>
                  );
                }

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '50px' }}>
                    {/* ISMS Requirements Section */}
                    {shouldShowRequirements && (
                      <Box>
                        <Typography variant="h5" sx={{
                          fontFamily: 'Roboto, Arial, sans-serif',
                          color: '#0A3D62',
                          fontWeight: 600,
                          fontSize: '1.3rem',
                          marginBottom: '25px',
                          paddingBottom: '10px',
                          borderBottom: '3px solid #0A3D62'
                        }}>
                          ISMS Requirements
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E0E0E0', marginBottom: '20px' }}>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#FAFAFA' }}>
                                <TableCell sx={{
                                  fontWeight: 600,
                                  color: '#0A3D62',
                                  fontSize: '0.85rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  borderBottom: '2px solid #E0E0E0'
                                }}>
                                  Requirement
                                </TableCell>
                                <TableCell sx={{
                                  fontWeight: 600,
                                  color: '#0A3D62',
                                  fontSize: '0.85rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  borderBottom: '2px solid #E0E0E0',
                                  width: '120px',
                                  textAlign: 'center'
                                }}>
                                  Status
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {allRequirements.map((control) => (
                                <TableRow
                                  key={control.id}
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: '#FAFAFA'
                                    },
                                    '&:last-child td': {
                                      borderBottom: 'none'
                                    }
                                  }}
                                >
                                  <TableCell sx={{ padding: '20px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
                                      {tabValue === 1 && (
                                        <Typography variant="body2" sx={{
                                          fontWeight: 600,
                                          color: '#0A3D62',
                                          fontSize: '0.85rem',
                                          fontFamily: 'monospace',
                                          minWidth: '40px'
                                        }}>
                                          {control.id}
                                        </Typography>
                                      )}
                                      <Typography variant="body1" sx={{
                                        fontWeight: 600,
                                        color: '#333',
                                        fontSize: '0.95rem'
                                      }}>
                                        {control.name}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{
                                      color: '#666',
                                      lineHeight: 1.6,
                                      fontSize: '0.875rem'
                                    }}>
                                      {control.description}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ padding: '20px', textAlign: 'center' }}>
                                    {control.status === 'Implemented' ? (
                                      <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '28px' }} />
                                    ) : control.status === 'Excluded' ? (
                                      <Typography variant="body2" sx={{
                                        color: '#9e9e9e',
                                        fontWeight: 500,
                                        fontSize: '0.8rem'
                                      }}>
                                        Excluded
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{
                                        color: control.status === 'In Progress' ? '#ff9800' : control.status === 'Planned' ? '#2196f3' : '#9e9e9e',
                                        fontWeight: 500,
                                        fontSize: '0.8rem'
                                      }}>
                                        {control.status}
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                  {/* Annex A Controls Section */}
                  {selectedCategory !== 'ISMS Requirements' && categoriesToDisplay.some(cat => groupedControls.Controls[cat] && groupedControls.Controls[cat].length > 0) && (
                    <Box>
                      <Typography variant="h5" sx={{
                        fontFamily: 'Roboto, Arial, sans-serif',
                        color: '#0A3D62',
                        fontWeight: 600,
                        fontSize: '1.3rem',
                        marginBottom: '25px',
                        paddingBottom: '10px',
                        borderBottom: '3px solid #0A3D62'
                      }}>
                        Annex A Controls
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                        {categoriesToDisplay.map((category) => {
                          const categoryControls = groupedControls.Controls[category] || [];
                          if (categoryControls.length === 0) return null;

                          return (
                            <Box key={`ctrl-${category}`}>
                              <Typography variant="h6" sx={{
                                fontFamily: 'Roboto, Arial, sans-serif',
                                color: '#0A3D62',
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                marginBottom: '15px',
                                paddingBottom: '10px',
                                borderBottom: '2px solid #0A3D62'
                              }}>
                                {category}
                              </Typography>
                              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E0E0E0', marginBottom: '20px' }}>
                                <Table>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: '#FAFAFA' }}>
                                      <TableCell sx={{
                                        fontWeight: 600,
                                        color: '#0A3D62',
                                        fontSize: '0.85rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        borderBottom: '2px solid #E0E0E0'
                                      }}>
                                        Control
                                      </TableCell>
                                      <TableCell sx={{
                                        fontWeight: 600,
                                        color: '#0A3D62',
                                        fontSize: '0.85rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        borderBottom: '2px solid #E0E0E0',
                                        width: '120px',
                                        textAlign: 'center'
                                      }}>
                                        Status
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {categoryControls.map((control) => (
                                      <TableRow
                                        key={control.id}
                                        sx={{
                                          '&:hover': {
                                            backgroundColor: '#FAFAFA'
                                          },
                                          '&:last-child td': {
                                            borderBottom: 'none'
                                          }
                                        }}
                                      >
                                        <TableCell sx={{ padding: '20px' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
                                            {tabValue === 1 && (
                                              <Typography variant="body2" sx={{
                                                fontWeight: 600,
                                                color: '#0A3D62',
                                                fontSize: '0.85rem',
                                                fontFamily: 'monospace',
                                                minWidth: '40px'
                                              }}>
                                                {control.id}
                                              </Typography>
                                            )}
                                            <Typography variant="body1" sx={{
                                              fontWeight: 600,
                                              color: '#333',
                                              fontSize: '0.95rem'
                                            }}>
                                              {control.name}
                                            </Typography>
                                          </Box>
                                          <Typography variant="body2" sx={{
                                            color: '#666',
                                            lineHeight: 1.6,
                                            fontSize: '0.875rem'
                                          }}>
                                            {control.description}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ padding: '20px', textAlign: 'center' }}>
                                          {control.status === 'Implemented' ? (
                                            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '28px' }} />
                                          ) : control.status === 'Excluded' ? (
                                            <Typography variant="body2" sx={{
                                              color: '#9e9e9e',
                                              fontWeight: 500,
                                              fontSize: '0.8rem'
                                            }}>
                                              Excluded
                                            </Typography>
                                          ) : (
                                            <Typography variant="body2" sx={{
                                              color: control.status === 'In Progress' ? '#ff9800' : control.status === 'Planned' ? '#2196f3' : '#9e9e9e',
                                              fontWeight: 500,
                                              fontSize: '0.8rem'
                                            }}>
                                              {control.status}
                                            </Typography>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  </Box>
                );
              })()}
            </Box>
          </Box>
        )}

        {/* Privacy Controls Tab */}
        {tabValue === 2 && (
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
            {/* Left Sidebar - Categories */}
            <Box sx={{
              width: isMobile ? '100%' : '250px',
              backgroundColor: '#FAFAFA',
              borderRight: isMobile ? 'none' : '1px solid #E0E0E0',
              borderBottom: isMobile ? '1px solid #E0E0E0' : 'none',
              minHeight: isMobile ? 'auto' : 'calc(100vh - 200px)',
              padding: '20px 0',
              marginRight: isMobile ? 0 : '30px',
              marginBottom: isMobile ? '20px' : 0
            }}>
              <Typography variant="h6" sx={{
                padding: '0 20px 15px 20px',
                color: '#0A3D62',
                fontFamily: 'Roboto, Arial, sans-serif',
                fontWeight: 600,
                fontSize: '0.95rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Controls
              </Typography>
              <List sx={{ padding: 0 }}>
                {categories.map((category, index) => (
                  <React.Fragment key={category}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => setSelectedCategory(category)}
                        selected={selectedCategory === category}
                        sx={{
                          padding: '12px 20px',
                          '&.Mui-selected': {
                            backgroundColor: '#E3F2FD',
                            borderLeft: '3px solid #0A3D62',
                            paddingLeft: '17px'
                          },
                          '&:hover': {
                            backgroundColor: '#F5F5F5'
                          }
                        }}
                      >
                        <ListItemText
                          primary={category}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: selectedCategory === category ? 600 : 400,
                            color: selectedCategory === category ? '#0A3D62' : '#333'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < categories.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>

            {/* Main Content Area */}
            <Box sx={{ flex: 1 }}>
              {/* Category Title and Search */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '30px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '20px'
              }}>
                <Typography variant="h5" sx={{
                  fontFamily: 'Roboto, Arial, sans-serif',
                  color: '#0A3D62',
                  fontWeight: 600,
                  fontSize: isMobile ? '1.3rem' : '1.5rem'
                }}>
                  {selectedCategory === 'All' ? 'All Controls' : selectedCategory}
                </Typography>
                <TextField
                  placeholder="Search controls"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#999', fontSize: '20px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: isMobile ? '100%' : '300px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#FAFAFA',
                      '&:hover fieldset': {
                        borderColor: '#0A3D62',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0A3D62',
                      },
                    },
                  }}
                />
              </Box>

              {/* Controls Tables - One per Category */}
              {categoriesToDisplay.length === 0 || categoriesToDisplay.every(cat => !groupedControls[cat] || groupedControls[cat].length === 0) ? (
                <Paper elevation={0} sx={{ padding: '60px', textAlign: 'center', backgroundColor: '#FAFAFA' }}>
                  <Typography variant="h6" sx={{ color: '#666', marginBottom: '10px' }}>
                    No controls found
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Try adjusting your search or category filter.
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                  {categoriesToDisplay.map((category) => {
                    const categoryControls = groupedControls[category] || [];
                    if (categoryControls.length === 0) return null;

                    return (
                      <Box key={category}>
                        <Typography variant="h6" sx={{
                          fontFamily: 'Roboto, Arial, sans-serif',
                          color: '#0A3D62',
                          fontWeight: 600,
                          fontSize: '1.1rem',
                          marginBottom: '15px',
                          paddingBottom: '10px',
                          borderBottom: '2px solid #0A3D62'
                        }}>
                          {category}
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E0E0E0', marginBottom: '20px' }}>
                          <Table>
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#FAFAFA' }}>
                                <TableCell sx={{
                                  fontWeight: 600,
                                  color: '#0A3D62',
                                  fontSize: '0.85rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  borderBottom: '2px solid #E0E0E0'
                                }}>
                                  Control
                                </TableCell>
                                <TableCell sx={{
                                  fontWeight: 600,
                                  color: '#0A3D62',
                                  fontSize: '0.85rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  borderBottom: '2px solid #E0E0E0',
                                  width: '120px',
                                  textAlign: 'center'
                                }}>
                                  Status
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {categoryControls.map((control) => (
                                <TableRow
                                  key={control.id}
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: '#FAFAFA'
                                    },
                                    '&:last-child td': {
                                      borderBottom: 'none'
                                    }
                                  }}
                                >
                                  <TableCell sx={{ padding: '20px' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
                                      {tabValue === 1 && (
                                        <Typography variant="body2" sx={{
                                          fontWeight: 600,
                                          color: '#0A3D62',
                                          fontSize: '0.85rem',
                                          fontFamily: 'monospace',
                                          minWidth: '40px'
                                        }}>
                                          {control.id}
                                        </Typography>
                                      )}
                                      <Typography variant="body1" sx={{
                                        fontWeight: 600,
                                        color: '#333',
                                        fontSize: '0.95rem'
                                      }}>
                                        {control.name}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{
                                      color: '#666',
                                      lineHeight: 1.6,
                                      fontSize: '0.875rem'
                                    }}>
                                      {control.description}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ padding: '20px', textAlign: 'center' }}>
                                    {control.status === 'Implemented' ? (
                                      <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '28px' }} />
                                    ) : control.status === 'Excluded' ? (
                                      <Typography variant="body2" sx={{
                                        color: '#9e9e9e',
                                        fontWeight: 500,
                                        fontSize: '0.8rem'
                                      }}>
                                        Excluded
                                      </Typography>
                                    ) : (
                                      <Typography variant="body2" sx={{
                                        color: control.status === 'In Progress' ? '#ff9800' : control.status === 'Planned' ? '#2196f3' : '#9e9e9e',
                                        fontWeight: 500,
                                        fontSize: '0.8rem'
                                      }}>
                                        {control.status}
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Documents Tab */}
        {tabValue === 3 && (
          <Box>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '30px',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '20px'
            }}>
              <Typography variant="h5" sx={{
                fontFamily: 'Roboto, Arial, sans-serif',
                color: '#0A3D62',
                fontWeight: 600,
                fontSize: isMobile ? '1.3rem' : '1.5rem'
              }}>
                Documents
              </Typography>
              <TextField
                placeholder="Search documents"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999', fontSize: '20px' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: isMobile ? '100%' : '300px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#FAFAFA',
                    '&:hover fieldset': {
                      borderColor: '#0A3D62',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0A3D62',
                    },
                  },
                }}
              />
            </Box>

            {/* Documents List */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E0E0E0' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#FAFAFA' }}>
                    <TableCell sx={{
                      fontWeight: 600,
                      color: '#0A3D62',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid #E0E0E0'
                    }}>
                      Document Name
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      color: '#0A3D62',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid #E0E0E0'
                    }}>
                      Type
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      color: '#0A3D62',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid #E0E0E0'
                    }}>
                      Date
                    </TableCell>
                    <TableCell sx={{
                      fontWeight: 600,
                      color: '#0A3D62',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid #E0E0E0',
                      width: '150px',
                      textAlign: 'center'
                    }}>
                      Access
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allDocuments
                    .filter(doc => 
                      !searchQuery || 
                      doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      doc.type.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((doc) => (
                      <TableRow
                        key={doc.id}
                        onClick={() => handleDocumentClick(doc)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: '#FAFAFA'
                          },
                          '&:last-child td': {
                            borderBottom: 'none'
                          }
                        }}
                      >
                        <TableCell sx={{ padding: '20px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DescriptionIcon sx={{ color: '#0A3D62', fontSize: '24px' }} />
                            <Typography variant="body1" sx={{
                              fontWeight: 500,
                              color: '#333',
                              fontSize: '0.95rem'
                            }}>
                              {doc.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ padding: '20px' }}>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                            {doc.type}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: '20px' }}>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                            {new Date(doc.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: '20px', textAlign: 'center' }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: '6px', 
                            color: doc.isPublic ? '#4caf50' : '#FF6B35',
                            cursor: 'pointer'
                          }}>
                            {doc.isPublic ? (
                              <>
                                <CheckCircleIcon sx={{ fontSize: '16px' }} />
                                <OpenInNewIcon sx={{ fontSize: '14px', opacity: 0.7 }} />
                              </>
                            ) : (
                              <LockIcon sx={{ fontSize: '16px' }} />
                            )}
                            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                              {doc.access}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default TrustCentre;

